/**
 * Simple ZIP file generator and parser using browser APIs
 * Creates and reads ZIP files from/to a dictionary of file paths to content
 */

// CRC32 lookup table
const CRC32_TABLE = (() => {
  const table: number[] = [];
  for (let n = 0; n < 256; n++) {
    let c = n;
    for (let k = 0; k < 8; k++) {
      c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    }
    table[n] = c;
  }
  return table;
})();

// Calculate CRC32 of data
function crc32(data: Uint8Array): number {
  let crc = 0xffffffff;
  for (let i = 0; i < data.length; i++) {
    crc = CRC32_TABLE[(crc ^ data[i]) & 0xff] ^ (crc >>> 8);
  }
  return (crc ^ 0xffffffff) >>> 0;
}

// Convert string to Uint8Array (UTF-8)
function stringToBytes(str: string): Uint8Array {
  return new TextEncoder().encode(str);
}

// Write a 16-bit little-endian value
function writeUint16LE(value: number): Uint8Array {
  const arr = new Uint8Array(2);
  arr[0] = value & 0xff;
  arr[1] = (value >> 8) & 0xff;
  return arr;
}

// Write a 32-bit little-endian value
function writeUint32LE(value: number): Uint8Array {
  const arr = new Uint8Array(4);
  arr[0] = value & 0xff;
  arr[1] = (value >> 8) & 0xff;
  arr[2] = (value >> 16) & 0xff;
  arr[3] = (value >> 24) & 0xff;
  return arr;
}

// Read a 16-bit little-endian value
function readUint16LE(data: Uint8Array, offset: number): number {
  return data[offset] | (data[offset + 1] << 8);
}

// Read a 32-bit little-endian value
function readUint32LE(data: Uint8Array, offset: number): number {
  return (
    data[offset] |
    (data[offset + 1] << 8) |
    (data[offset + 2] << 16) |
    (data[offset + 3] << 24)
  ) >>> 0;
}

// Convert Uint8Array to string (UTF-8)
function bytesToString(bytes: Uint8Array): string {
  return new TextDecoder().decode(bytes);
}

// Concatenate multiple Uint8Arrays
function concat(...arrays: Uint8Array[]): Uint8Array {
  const totalLength = arrays.reduce((sum, arr) => sum + arr.length, 0);
  const result = new Uint8Array(totalLength);
  let offset = 0;
  for (const arr of arrays) {
    result.set(arr, offset);
    offset += arr.length;
  }
  return result;
}

type ZipEntry = {
  path: string;
  content: string;
};

/**
 * Create a ZIP file from a list of files
 * @param files - Array of { path, content } objects
 * @param projectName - Name for the ZIP file (without .zip extension)
 * @returns Blob containing the ZIP file
 */
export function createZip(files: ZipEntry[], projectName: string = 'project'): Blob {
  const centralDirectory: Uint8Array[] = [];
  const fileData: Uint8Array[] = [];
  let offset = 0;
  const now = new Date();

  // DOS date/time format
  const dosTime = ((now.getHours() << 11) | (now.getMinutes() << 5) | (now.getSeconds() >> 1)) & 0xffff;
  const dosDate = (((now.getFullYear() - 1980) << 9) | ((now.getMonth() + 1) << 5) | now.getDate()) & 0xffff;

  for (const file of files) {
    const pathBytes = stringToBytes(file.path);
    const contentBytes = stringToBytes(file.content);
    const crc = crc32(contentBytes);
    const compressedSize = contentBytes.length;
    const uncompressedSize = contentBytes.length;

    // Local file header
    const localHeader = concat(
      new Uint8Array([0x50, 0x4b, 0x03, 0x04]), // Local file header signature
      writeUint16LE(20),                         // Version needed to extract (2.0)
      writeUint16LE(0x0800),                     // General purpose bit flag (UTF-8 filename)
      writeUint16LE(0),                          // Compression method (stored)
      writeUint16LE(dosTime),                    // Last mod file time
      writeUint16LE(dosDate),                    // Last mod file date
      writeUint32LE(crc),                        // CRC-32
      writeUint32LE(compressedSize),             // Compressed size
      writeUint32LE(uncompressedSize),           // Uncompressed size
      writeUint16LE(pathBytes.length),           // File name length
      writeUint16LE(0),                          // Extra field length
      pathBytes,                                 // File name
      contentBytes                               // File data
    );

    fileData.push(localHeader);

    // Central directory file header
    const centralHeader = concat(
      new Uint8Array([0x50, 0x4b, 0x01, 0x02]), // Central file header signature
      writeUint16LE(20),                         // Version made by (2.0)
      writeUint16LE(20),                         // Version needed to extract (2.0)
      writeUint16LE(0x0800),                     // General purpose bit flag (UTF-8 filename)
      writeUint16LE(0),                          // Compression method (stored)
      writeUint16LE(dosTime),                    // Last mod file time
      writeUint16LE(dosDate),                    // Last mod file date
      writeUint32LE(crc),                        // CRC-32
      writeUint32LE(compressedSize),             // Compressed size
      writeUint32LE(uncompressedSize),           // Uncompressed size
      writeUint16LE(pathBytes.length),           // File name length
      writeUint16LE(0),                          // Extra field length
      writeUint16LE(0),                          // File comment length
      writeUint16LE(0),                          // Disk number start
      writeUint16LE(0),                          // Internal file attributes
      writeUint32LE(0),                          // External file attributes
      writeUint32LE(offset),                     // Relative offset of local header
      pathBytes                                  // File name
    );

    centralDirectory.push(centralHeader);
    offset += localHeader.length;
  }

  const centralDirData = concat(...centralDirectory);
  const centralDirOffset = offset;
  const centralDirSize = centralDirData.length;

  // End of central directory record
  const endRecord = concat(
    new Uint8Array([0x50, 0x4b, 0x05, 0x06]), // End of central dir signature
    writeUint16LE(0),                          // Number of this disk
    writeUint16LE(0),                          // Disk where central directory starts
    writeUint16LE(files.length),               // Number of central directory records on this disk
    writeUint16LE(files.length),               // Total number of central directory records
    writeUint32LE(centralDirSize),             // Size of central directory
    writeUint32LE(centralDirOffset),           // Offset of start of central directory
    writeUint16LE(0)                           // Comment length
  );

  const zipData = concat(...fileData, centralDirData, endRecord);
  return new Blob([zipData], { type: 'application/zip' });
}

/**
 * Download a ZIP file
 * @param blob - The ZIP blob to download
 * @param filename - The filename for the download
 */
export function downloadZip(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename.endsWith('.zip') ? filename : `${filename}.zip`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/**
 * Export project files as a ZIP download
 * @param files - Record of file paths to content
 * @param projectName - Name for the ZIP file
 */
export function exportProjectAsZip(
  files: Record<string, string>,
  projectName: string = 'project'
): void {
  const entries: ZipEntry[] = Object.entries(files).map(([path, content]) => ({
    path,
    content,
  }));

  if (entries.length === 0) {
    console.warn('No files to export');
    return;
  }

  const blob = createZip(entries, projectName);
  downloadZip(blob, projectName);
}

/**
 * Find the end of central directory record in a ZIP file
 */
function findEndOfCentralDirectory(data: Uint8Array): number {
  // Search backwards for the signature 0x06054b50
  const signature = [0x50, 0x4b, 0x05, 0x06];
  for (let i = data.length - 22; i >= 0; i--) {
    if (
      data[i] === signature[0] &&
      data[i + 1] === signature[1] &&
      data[i + 2] === signature[2] &&
      data[i + 3] === signature[3]
    ) {
      return i;
    }
  }
  return -1;
}

/**
 * Parse a local file header and return the file entry
 */
function parseLocalFileHeader(
  data: Uint8Array,
  offset: number
): { path: string; content: Uint8Array; nextOffset: number } | null {
  // Check signature
  if (
    data[offset] !== 0x50 ||
    data[offset + 1] !== 0x4b ||
    data[offset + 2] !== 0x03 ||
    data[offset + 3] !== 0x04
  ) {
    return null;
  }

  const compressionMethod = readUint16LE(data, offset + 8);
  const compressedSize = readUint32LE(data, offset + 18);
  const fileNameLength = readUint16LE(data, offset + 26);
  const extraFieldLength = readUint16LE(data, offset + 28);

  const fileNameStart = offset + 30;
  const fileNameBytes = data.slice(fileNameStart, fileNameStart + fileNameLength);
  const path = bytesToString(fileNameBytes);

  const dataStart = fileNameStart + fileNameLength + extraFieldLength;
  const content = data.slice(dataStart, dataStart + compressedSize);

  // Only support stored (uncompressed) files for now
  if (compressionMethod !== 0) {
    console.warn(`Skipping compressed file: ${path} (method: ${compressionMethod})`);
    return {
      path,
      content: new Uint8Array(0),
      nextOffset: dataStart + compressedSize,
    };
  }

  return {
    path,
    content,
    nextOffset: dataStart + compressedSize,
  };
}

export type ImportedFile = {
  path: string;
  content: string;
  isDirectory: boolean;
};

/**
 * Parse a ZIP file and return its contents
 * @param data - The ZIP file data as Uint8Array
 * @returns Array of imported files
 */
export async function parseZip(data: Uint8Array): Promise<ImportedFile[]> {
  const endOfCentralDir = findEndOfCentralDirectory(data);
  if (endOfCentralDir === -1) {
    throw new Error('Invalid ZIP file: End of central directory not found');
  }

  // Note: centralDirOffset not used in this simple implementation
  // const centralDirOffset = readUint32LE(data, endOfCentralDir + 16);
  const totalEntries = readUint16LE(data, endOfCentralDir + 10);

  const files: ImportedFile[] = [];
  let offset = 0;

  // Parse local file headers
  for (let i = 0; i < totalEntries; i++) {
    const result = parseLocalFileHeader(data, offset);
    if (!result) break;

    const { path, content, nextOffset } = result;
    const isDirectory = path.endsWith('/');

    if (!isDirectory && content.length > 0) {
      files.push({
        path: path.replace(/^\/+/, ''), // Remove leading slashes
        content: bytesToString(content),
        isDirectory: false,
      });
    } else if (isDirectory) {
      files.push({
        path: path.replace(/^\/+/, '').replace(/\/+$/, ''), // Remove leading/trailing slashes
        content: '',
        isDirectory: true,
      });
    }

    offset = nextOffset;
  }

  return files;
}

/**
 * Read a ZIP file from a File object
 * @param file - The File object to read
 * @returns Promise resolving to array of imported files
 */
export function readZipFile(file: File): Promise<ImportedFile[]> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const data = new Uint8Array(e.target?.result as ArrayBuffer);
        const files = await parseZip(data);
        resolve(files);
      } catch (error) {
        reject(error);
      }
    };
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsArrayBuffer(file);
  });
}

/**
 * Strip the common root folder from imported paths if all files share it
 * @param files - Array of imported files
 * @returns Array with paths adjusted to remove common root
 */
export function normalizeImportedPaths(files: ImportedFile[]): ImportedFile[] {
  if (files.length === 0) return files;

  // Get all non-directory file paths
  const filePaths = files.filter(f => !f.isDirectory).map(f => f.path);
  if (filePaths.length === 0) return files;

  // Find the common prefix (directory)
  const parts = filePaths.map(p => p.split('/'));
  let commonPrefix: string[] = [];

  if (parts.length > 0 && parts[0].length > 1) {
    for (let i = 0; i < parts[0].length - 1; i++) {
      const part = parts[0][i];
      if (parts.every(p => p[i] === part)) {
        commonPrefix.push(part);
      } else {
        break;
      }
    }
  }

  if (commonPrefix.length === 0) return files;

  const prefixStr = commonPrefix.join('/') + '/';

  return files.map(f => ({
    ...f,
    path: f.path.startsWith(prefixStr) ? f.path.slice(prefixStr.length) : f.path,
  })).filter(f => f.path.length > 0); // Remove empty paths (the root folder itself)
}

/**
 * Convert imported files to a Record<string, string> format
 * @param files - Array of imported files
 * @returns Record mapping paths to content
 */
export function importedFilesToRecord(files: ImportedFile[]): Record<string, string> {
  const result: Record<string, string> = {};
  for (const file of files) {
    if (!file.isDirectory) {
      result[file.path] = file.content;
    }
  }
  return result;
}
